import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, setTokens, getAccessToken } from './api';
import { clearShopChoice } from './shopChoice';
import { DeviceUntrustedScreen } from '../modules/auth/DeviceUntrustedScreen';
import type { PermissionKey, UserRole } from '@kalachand/shared';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  companyId: string;
  homeShopId: string | null;
  homeShop: { id: string; code: string; name: string } | null;
  // homeShopId plus every shop from an active UserShopAssignment (e.g. a
  // branch seller also granted the ONLINE shop by an admin).
  allowedShopIds: string[];
  // Which allowed shop the user is currently "at" — switchable any time via
  // setActiveShop, remembered across logins.
  activeShopId: string | null;
  // The resolved active shop (code/name), so the whole app can show which shop
  // she's working from. Same source as activeShopId.
  activeShop: { id: string; code: string; name: string } | null;
  // Sandbox account — see backend User.isTest. When true the app shows the
  // TestModeBanner and the sandbox⇄live view toggle.
  isTest?: boolean;
}

/** Result of the password step: either signed in, or a 2FA challenge to finish. */
export type LoginOutcome =
  | { status: 'ok' }
  | { status: 'mfa'; mode: 'verify' | 'setup'; challengeToken: string };

export interface AuthState {
  user: AuthUser | null;
  perms: Set<PermissionKey>;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  verifyTotp: (challengeToken: string, code: string) => Promise<void>;
  setupTotp: (challengeToken: string) => Promise<{ secret: string; otpauthUri: string }>;
  enableTotp: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  // Switch "which shop am I at" — server validates it's one of the caller's
  // own allowed shops, persists it, and returns a fresh session so the switch
  // takes effect immediately across the app.
  setActiveShop: (shopId: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface MeResponse {
  user: AuthUser;
  perms: PermissionKey[];
}

interface SessionPayload {
  status: 'ok';
  user: AuthUser;
  perms: PermissionKey[];
  accessToken: string;
  refreshToken: string;
}
type LoginPayload =
  | SessionPayload
  | { status: 'mfa'; mode: 'verify' | 'setup'; challengeToken: string };

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const qc = useQueryClient();
  const [bootstrapped, setBootstrapped] = useState(false);
  // Token lives in React state (seeded from localStorage) so clearing it on
  // logout / 401 triggers an immediate re-render — otherwise `user` stays
  // stale until a manual page refresh re-reads localStorage.
  const [token, setTokenState] = useState<string | null>(() => getAccessToken());
  // Set when the API reports the current device isn't trusted → full takeover.
  const [deviceBlocked, setDeviceBlocked] = useState(false);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => apiGet<MeResponse>('/auth/me'),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    setBootstrapped(true);
    const handler = (): void => {
      setTokens(null, null);
      setTokenState(null);
      setDeviceBlocked(false);
      clearShopChoice();
      qc.removeQueries({ queryKey: ['me'] });
    };
    const deviceHandler = (): void => setDeviceBlocked(true);
    window.addEventListener('kalachand:unauthorized', handler);
    window.addEventListener('kalachand:device-untrusted', deviceHandler);
    return () => {
      window.removeEventListener('kalachand:unauthorized', handler);
      window.removeEventListener('kalachand:device-untrusted', deviceHandler);
    };
  }, [qc]);

  const applySession = (s: SessionPayload): void => {
    setTokens(s.accessToken, s.refreshToken);
    setTokenState(s.accessToken);
    setDeviceBlocked(false);
    qc.setQueryData(['me'], { user: s.user, perms: s.perms });
  };

  const login = async (email: string, password: string): Promise<LoginOutcome> => {
    const { data } = await api.post<{ data: LoginPayload }>('/auth/login', { email, password });
    if (data.data.status === 'ok') {
      applySession(data.data);
      return { status: 'ok' };
    }
    return { status: 'mfa', mode: data.data.mode, challengeToken: data.data.challengeToken };
  };

  const verifyTotp = async (challengeToken: string, code: string): Promise<void> => {
    const { data } = await api.post<{ data: SessionPayload }>('/auth/login/verify', {
      challengeToken,
      code,
    });
    applySession(data.data);
  };

  const setupTotp = async (
    challengeToken: string,
  ): Promise<{ secret: string; otpauthUri: string }> => {
    const { data } = await api.post<{ data: { secret: string; otpauthUri: string } }>(
      '/auth/2fa/setup',
      {
        challengeToken,
      },
    );
    return data.data;
  };

  const enableTotp = async (challengeToken: string, code: string): Promise<void> => {
    // During login enrolment the server completes the sign-in and returns a session.
    const { data } = await api.post<{ data: SessionPayload }>('/auth/2fa/enable', {
      challengeToken,
      code,
    });
    applySession(data.data);
  };

  const setActiveShop = async (shopId: string): Promise<void> => {
    const { data } = await api.patch<{ data: SessionPayload }>('/auth/active-shop', { shopId });
    applySession(data.data);
    // Every list/default that reads shop context comes from the JWT now, so a
    // switch invalidates everything rather than trying to enumerate affected
    // query keys — the app quietly refetches against the new shop.
    void qc.invalidateQueries();
  };

  const logout = async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setTokens(null, null);
    setTokenState(null);
    setDeviceBlocked(false);
    clearShopChoice();
    qc.removeQueries({ queryKey: ['me'] });
  };

  const value: AuthState = {
    // Guard on `token` so the instant it's cleared, `user` is null — even if
    // the cached query data hasn't been evicted yet.
    user: token ? (meQuery.data?.user ?? null) : null,
    perms: new Set<PermissionKey>(token ? (meQuery.data?.perms ?? []) : []),
    loading: !bootstrapped || (!!token && meQuery.isLoading),
    login,
    verifyTotp,
    setupTotp,
    enableTotp,
    logout,
    setActiveShop,
  };

  return (
    <AuthContext.Provider value={value}>
      {deviceBlocked ? <DeviceUntrustedScreen onLogout={() => void logout()} /> : children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
